//React
import React, { useState } from 'react';

// Futurelab
import { RootState } from '../../reducers';
import { useSelector } from 'react-redux';
import { UserDetailsListItem } from '../../model';
import { useLazyGetProgramDetailsQuery, useLazyGetUserDetailsListQuery } from '../../store/api/userApi';
import { ProgramDetailsInterface } from '../../model/exportTypeUtil';
import avatarPlaceholder from '../../assets/image/avatar_placeholder.png'

// UI
import { ArrowRight, BriefcaseBusiness } from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';

interface GroupDetailsItemPopup {
	displayGroupDetails: any;
	setDisplayGroupDetails: any;
	getGroupChatNameInitials: any;
	getGroupChatName: any;
}

function GroupDetails(GroupDetailsItemPopup: GroupDetailsItemPopup) {
	const { displayGroupDetails, setDisplayGroupDetails, getGroupChatNameInitials, getGroupChatName } =
		GroupDetailsItemPopup;
	const chatInFocus = useSelector((state: RootState) => state.chatInFocus);
	const loggedInUserDetails = useSelector((state: RootState) => state.loggedInUserDetails);
	const handleClose = () => setDisplayGroupDetails(!displayGroupDetails);
	const photoUrl = `${process.env.REACT_APP_PROFILE_PHOTOS_BASE_URL}${process.env.REACT_APP_PROFILE_PHOTOS_S3_BASE_URL}${process.env.REACT_APP_PROFILE_PHOTOS_S3_DIRECTORY_URL}`;
	const [programDetails, setProgramDetails] = useState<ProgramDetailsInterface | null>(null);
	const [chatListParticipantsDetails, setChatListParticipantsDetails] = useState<UserDetailsListItem[]>([]);
	const [loadProgramDetails] = useLazyGetProgramDetailsQuery();
	const [loadUserDetailsList] = useLazyGetUserDetailsListQuery();

	React.useEffect(() => {
		const fetchGroupChatModalData = async () => {
			try {
				const [usersDetails, programDetails] = await Promise.all([
					loadUserDetailsList(listOfrecipientDetails).unwrap(),
					chatInFocus.programId ? loadProgramDetails(chatInFocus.programId).unwrap() : Promise.resolve(null),
				]);
				setProgramDetails(programDetails);
				setChatListParticipantsDetails(usersDetails);
			} catch (error) {
				console.error(error);
			}
		};

		const listOfrecipientDetails = chatInFocus?.participants
			// commenting out this line so that we can get the details of the logged in user as well
			// .filter((participant) => participant.userId !== loggedInUserDetails.id)
			.map(({ userId }) => {
				return userId;
			});
		fetchGroupChatModalData();
	}, [chatInFocus, loadProgramDetails, loadUserDetailsList]);

	const isAdmin = (chatListParticipantDetails: UserDetailsListItem): boolean | undefined => {
		const targetUser = chatInFocus?.participants.find(
			(participant) => participant.userId === chatListParticipantDetails.id
		);
		return targetUser?.isAdmin;
	};

	const returnUserRoleInGroupChat = (chatListParticipantDetails: UserDetailsListItem): string => {
		const userRoleTarget = chatListParticipantDetails.userRoles.find(
			(userRole) => userRole.programId === programDetails?.id
		);
		return userRoleTarget?.description || '';
	};

	const goToProgram = () => {
		if (programDetails?.programEnterprises?.slug && programDetails?.slug) {
			window.open(
				`${process.env.REACT_APP_PORTAL_BASE_URL}${programDetails?.programEnterprises?.slug}/${programDetails?.slug}`,
				'_blank'
			);
		}
	};

	const displayAdminMembersList = () => {
		const chatAdmins = chatListParticipantsDetails.filter((chatListParticipantDetails) =>
			isAdmin(chatListParticipantDetails)
		);
		return renderMembersList(chatAdmins);
	};

	const displayNonAdminMembersList = () => {
		const chatNonAdmins = chatListParticipantsDetails.filter(
			(chatListParticipantDetails) => !isAdmin(chatListParticipantDetails)
		);
		return renderMembersList(chatNonAdmins);
	};

	const renderMembersList = (chatListParticipantsDetailsArranged: UserDetailsListItem[]) => {
		return chatListParticipantsDetailsArranged.map((chatListParticipantDetails) => {
			return (
				<li className="flex min-w-0 gap-3 pb-3" key={chatListParticipantDetails.id.toString()}>
					<div className="shrink-0">
								{!!chatListParticipantDetails?.profile_photo ? (
									<img
										src={`${photoUrl}/${chatListParticipantDetails?.id}/${chatListParticipantDetails?.profile_photo}`}
										className="chat-list-image-holder"
										alt=""
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = avatarPlaceholder;
                    }}
									/>
								) : (
									<Avatar className="h-[46px] w-[46px] rounded-md">
										<AvatarFallback className="rounded-md">
										{chatListParticipantDetails.firstname.trim()?.charAt(0)?.toLocaleUpperCase()}
										{chatListParticipantDetails.lastname.trim()?.charAt(0)?.toLocaleUpperCase() ?? ''}
										</AvatarFallback>
									</Avatar>
								)}
					</div>
					<div className="min-w-0 flex-1">
						<div className="group-users-name mb-1.5">
										{chatListParticipantDetails.firstname} {chatListParticipantDetails.lastname ?? ''}{' '}
										{isAdmin(chatListParticipantDetails) ? (
											<span className="group-admin-status">
												{' '}
												{returnUserRoleInGroupChat(chatListParticipantDetails)}
											</span>
										) : (
											''
										)}
								</div>
								<div className="flex min-w-0 items-center gap-2 pt-0.5">
									<BriefcaseBusiness className="size-4 shrink-0 text-muted-foreground" />
									<span className="min-w-0 break-words">
										{chatListParticipantDetails?.userInfos?.jobTitle ?? ''}
									</span>
								</div>
					</div>
				</li>
			);
		});
	};

	return (
		<Dialog open={displayGroupDetails} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] min-w-0 overflow-x-hidden overflow-y-auto p-0 sm:max-w-sm" showCloseButton>
				<DialogHeader className="sr-only">
					<DialogTitle>Group details</DialogTitle>
					<DialogDescription>Group chat information and members.</DialogDescription>
				</DialogHeader>
				<div className="mb-5 rounded-t-xl bg-[#4E9E57] py-3 text-center text-7xl text-white">
					{getGroupChatNameInitials().toLocaleUpperCase()}
				</div>
				<div className="min-w-0 px-5">
				<div className="group-chat-name break-words">
					{getGroupChatName()}
				</div>
				<div className="group-chat-description break-words">
					{chatInFocus.description}
				</div>
				</div>

			{programDetails?.programEnterprises?.slug && programDetails?.slug ? (
				<div className="programme-type-container min-w-0">
					<div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
						<div className="programme-label">
							Programme
						</div>
						<button type="button" className="go-to-programme-link inline-flex items-center" onClick={goToProgram}>
							Go to programme
							<ArrowRight className="ml-1 size-4" />
						</button>
					</div>
					<p className="programme-name break-words">{programDetails?.name}</p>
					{programDetails?.programType ? <p className="programme-type-label">Programme Type</p> : ''}
					<p className="programme-type break-words">{programDetails?.programType}</p>
				</div>
			) : (
				''
			)}
			<div className="group-members-container px-5 pb-5">
				<div className="group-members-and-count">
					Members ({chatListParticipantsDetails.length})
				</div>
				<ul className="mt-3">
				{displayAdminMembersList()}
				{displayNonAdminMembersList()}
				</ul>
			</div>
			</DialogContent>
		</Dialog>
	);
}

export default GroupDetails;
